import { SetType, SetSuccess, PostSetStatus, PreSetStatus, remapOldPostSetStatusNumberToEnum } from '../enums';
import { SetStats } from './set-stats';
import { IntensityLevel, Plan_ExercisePath, Plan_SubSlot } from './fitness-plan';
import { DateTime } from 'luxon';
import { assureIdForEntry, LogRecordState, LogRecordType, Log_EntryBase } from './user-log';
import { deepClone } from '../deep-helpers';

// Used for Myo-reps
export interface Log_WorkoutMiniSet {
  plannedReps: number;
  reps?: number; // actually done
}

export interface Log_WorkoutSetPostStatus {
  status: PostSetStatus | number;
  textInserts?: {
    [key: string]: string;
  };
  dismissed?: boolean;
}

export interface Log_WorkoutSetPreStatus {
  status: PreSetStatus | number;
  textInserts?: {
    [key: string]: string;
  };
  dismissed?: boolean;
}

/** One intensity's strength-retest demand as it stood before a set was concluded — see
 *  `Log_WorkoutSet.otherIntensityRetestDemandBackup`. */
export interface Log_RetestDemandBackup {
  intensityLevel: IntensityLevel;
  nextWorkoutSetType: SetType;
  nextWorkoutNonRMTestRequired?: boolean;
}

export interface Log_WorkoutSet {
  state: LogRecordState; // this enum is normally used on entire entries, but similar progress is tracked for individual sets as well
  setType?: SetType;
  stats: SetStats;
  /** only used for some set types - signifies whether e.g. user achieved the target */
  setSuccess?: SetSuccess;
  plannedWeight_kg?: number;
  plannedReps?: number;
  // only if myoReps are to be used on this set
  miniSets?: Log_WorkoutMiniSet[];
  userNotes?: string;
  completedTime_utc?: string;
  postStatus: Log_WorkoutSetPostStatus[];
  preStatus: Log_WorkoutSetPreStatus[];
  // Backup of subslot when concluding the first set of exercise. This is used when doing edits of sets in the past.
  planPathBackup?: Plan_ExercisePath;
  /** df-72de01d6fc68: what every OTHER intensity of the exercise held for the strength-retest
   *  demand just before this set was concluded — one entry per intensity the session did not
   *  perform. `planPathBackup` records the performed intensity only, but a conclusion can write
   *  the retest demand onto every intensity, so a correction needs this to take it back from the
   *  others too. Absent on every set logged before the field existed and on a single-intensity
   *  exercise; both mean "restore nothing on the other intensities", which is what the app did
   *  before. See `design-docs/volume-autoregulation.md`, "Correcting an already-finished set". */
  otherIntensityRetestDemandBackup?: Log_RetestDemandBackup[];
  /** Set when this set was prescribed the weight the user last lifted, with a reduced rep
   *  target, because the session followed a training break (#3339/#3924's rep-fallback
   *  branch). The plan path keeps its higher climbing `target_weight_kg` in that case, so
   *  without this record a correction to the set would be judged against a weight the app
   *  never asked for. Absent on every set logged before the field existed, and on every set
   *  outside that branch; both mean "judge against the path's own target", which is what the
   *  app did before. Since #5617 a new set carries it only when the break fell back to the
   *  older last-lifted-weight rule; otherwise it carries `layoffPrescribed`. Kept, and still
   *  read, for the sets logged before that. See `design-docs/reactive-deload.md`, "Related:
   *  #3924 layoff held-weight yardstick". */
  layoffHeldWeightWithReducedReps?: true;
  /** Set on the first set after a training break when the layoff weight was worked out from the
   *  user's best recent set (#5617). That weight is stored as the path's `target_weight_kg` when
   *  this set concludes, so the set is judged against its own target, and this record keeps one
   *  protection a correction would otherwise lose: the yardstick is never snapped ABOVE the weight
   *  the screen prefilled (`plannedWeight_kg`). Absent on sets logged before the field existed
   *  and on every set without that layoff; both mean the old reading. See
   *  `design-docs/reactive-deload.md`, "Layoff prescription from the best recent one-rep max". */
  layoffPrescribed?: true;
  /** Set when the user answered "No" to the reactive-deload question on this set (#6309). Such a
   *  set is stored as `SetSuccess.MinReps` and, since #6957, sets the rep floor to its reps + 1
   *  (before that the floor stayed where it was), the same result real progress gets, so the completed set's confirmation reads this to say "Set logged" instead of
   *  "You progressed!". Absent on sets logged before the field existed (the confirmation then falls
   *  back to a guess from the floor and the weight) and on every set that was not offered a
   *  deload. Cleared when an edited set is concluded again. See
   *  `design-docs/working-set-screen.md`, "The confirmation on a COMPLETED benchmark set". */
  deloadDeclined?: true;
  /** Set when a first set scored as `SetSuccess.MinReps` has a strength estimate that is not higher
   *  than the strongest set, failed or not, of the last session in which the exercise was really
   *  done, or when there is no such set to compare with (#6447). The result
   *  and the rep floor stay as they are; only the confirmation reads this, to say "Set logged"
   *  instead of "You progressed!". Absent on sets logged before the field existed (they keep the
   *  old reading). Cleared when an edited set is concluded again. See
   *  `design-docs/working-set-screen.md`, "The confirmation on a COMPLETED benchmark set". */
  noGainOverLastSession?: true;
  /** Set when the prescribed weight was LOWERED because the plan asked for more than the user's own
   *  recent performance says is possible for the prescribed rep count (#5176). The plan path keeps
   *  its higher climbing `target_weight_kg` — the cap is one session's observation, not a
   *  progression event — so without this record the user would be judged against the weight the app
   *  chose NOT to ask for, and lifting exactly what was on screen would be scored a miss. The capped
   *  weight itself is `plannedWeight_kg`. Absent on every set logged before the field existed and on
   *  every uncapped set; both mean "judge against the path's own target", which is what the app did
   *  before. */
  prescribedWeightCapped?: true;
}

export interface Log_WorkoutExercise {
  subSlotId: string;
  exerciseId?: string; // refers to official exercise if starts with "$", user-made otherwise
  pathLevel?: IntensityLevel;
  sets: Log_WorkoutSet[];
  numPlannedSets?: number; // if myoreps are used, this indirectly indicates actual myoreps based on the specs table
  // Backup of subslot when concluding the first set of exercise. This is used when doing edits of sets in the past.
  planSubslotBackup?: Plan_SubSlot;
  /** The exercise's program entry as this session LEFT it: re-taken at the end of every set
   *  conclusion, so it carries the progression the session earned (a strength test's result
   *  above all). `planSubslotBackup` above is the state the session STARTED from and stays as it
   *  is. Read only by the layoff restore that gives a swapped-back or re-added exercise its
   *  progression again (df-5dbef4889250). Taken by `buildAfterSessionSnapshot`, which strips any
   *  open plateau or volume question. Absent on sessions logged before it existed. See
   *  design-docs/exercise-replacement.md, "Layoff restore on re-add". */
  planSubslotAfterSession?: Plan_SubSlot;
  // if this exercise was temporarily swapped for this session only (plan not modified)
  isTempSwap?: boolean;
  tempSwapOriginalExerciseId?: string;
  /** set once the user has decisively answered — accepted or declined — a volume
   *  autoregulation suggestion that THIS workout raised for this exercise. It stops
   *  the app asking about the same exercise again in the same workout (#4714).
   *  Why it lives here rather than on the plan path, and why a plain flag is enough
   *  to keep each intensity path answering on its own:
   *  design-docs/volume-autoregulation.md, "One question per exercise per workout". */
  volumeAutoregulationAnswered?: true;
}

export interface Log_Workout extends Log_EntryBase {
  type: LogRecordType.Workout;
  /**
   * Index of workout in plan (A=0, B=1, etc.)
   * @deprecated Use WorkoutLoaderService.getEffectivePlanReference() to read, setWorkoutPlanReference() to write
   */
  planReference?: number;
  /**
   * @deprecated Use WorkoutLoaderService.getEffectiveExercises() to read, setWorkoutExercises() to write
   */
  exercises: Log_WorkoutExercise[];

  // used to restore state if app is reloaded while in workout page, or otherwise leaving and resuming the workout page
  lastUIState?: {
    currentExerciseId: string;
    currentSetIndex: number;
  }

  /**
   * UTC timestamp stamped by `WorkoutDetailPage` the first time a coach
   * (in manage mode) opens a strength-test workout. Absence = unreviewed —
   * no migration needed. Drives the "strength test — review" action-item
   * on the Coach Dashboard (`design-docs/coach-mode.md` → Coach Dashboard).
   */
  coachReviewedAtUtc?: string;
}

/**
 * Every set type that is part of a strength test, the follow-up set included.
 *
 * NOT the same set as `SET_TYPES_WITH_NO_TARGET_PRESCRIPTION` below, which leaves
 * `RMTestFollow` out. Read that one's comment before using either for a question about
 * whether a plan path is allowed to have no target weight.
 */
export const STRENGTH_TEST_SET_TYPES = new Set<SetType>([
  SetType.RMTest,
  SetType.RMTestFollow,
  SetType.NonRMTest,
  SetType.RepTest,
]);

/** Whether a single `SetType` qualifies as a strength-test set (RM-test / RM-test follow / rep-test / non-RM-test first set). */
export function isStrengthTestSetType(setType: SetType | number | undefined): boolean {
  return !!setType && STRENGTH_TEST_SET_TYPES.has(setType as SetType);
}

/**
 * The set types whose plan path legitimately carries NO target weight and no target reps:
 * measuring those numbers is what the set is for, so there is nothing to prescribe yet.
 *
 * `WorkoutLoaderService.fixupInvalidPathState` returns early for exactly these types instead
 * of healing the path into a retest and reporting the #3752 canary. Everything that has to
 * predict what that function will do reads this set, so the prediction cannot drift away from
 * the thing it predicts.
 *
 * Deliberately NOT `STRENGTH_TEST_SET_TYPES` above, which is the obvious helper to reach for
 * and answers differently: it also holds `RMTestFollow`. A follow-up set runs at a weight the
 * first test set already established, so its path IS expected to carry a target — and
 * `fixupInvalidPathState` does heal one that does not. Using the wider set here would let such
 * a path through a caller's own guard while the loader still healed it and fired the canary
 * for a condition nobody can act on.
 */
export const SET_TYPES_WITH_NO_TARGET_PRESCRIPTION = new Set<SetType>([
  SetType.RMTest,
  SetType.NonRMTest,
  SetType.RepTest,
]);

/** Whether a plan path of this set type is allowed to carry no target weight and no target reps. */
export function setTypeHasNoTargetPrescription(setType: SetType | number | undefined): boolean {
  return !!setType && SET_TYPES_WITH_NO_TARGET_PRESCRIPTION.has(setType as SetType);
}

/**
 * Shared predicate for "does this workout contain any strength-test set" —
 * used by both `WorkoutDetailPage.notifyCoachIfStrengthTest` and the Coach
 * Dashboard's action-item detector so the two sites agree on what counts
 * as a strength test.
 */
export function isStrengthTestWorkout(workout: Log_Workout): boolean {
  if (!workout?.exercises) return false;
  for (const ex of workout.exercises) {
    if (!ex?.sets) continue;
    for (const s of ex.sets) {
      if (isStrengthTestSetType(s?.setType)) return true;
    }
  }
  return false;
}

/**
 * Which end-of-workout completion message the workout-detail page should show.
 * The onboarding payoff copy (#4061) is sequenced across the first two
 * strength-test runs OF EACH SPLIT DAY; every other workout gets the generic
 * message.
 */
export enum WorkoutCompletionMessage {
  Generic = 'generic',
  FirstTest = 'firstTest',
  SecondTest = 'secondTest',
}

/**
 * Pick which completion message to show after a workout finishes (#4061).
 *
 * A training plan is a split: the user rotates through several different
 * workouts (A, B, C). The first two runs of ANY of those days are strength/rep
 * tests, because that day's exercises have not been trained before. We greet
 * those two with a sequenced payoff message ("Test workout done!" then
 * "Starting weights and rep targets set!") to confirm their progress.
 * Everything else — a non-test workout, or any run past the second one of that
 * same split day — keeps the generic "Great work!" message.
 *
 * The count is deliberately PER SPLIT DAY, not across the user's whole history.
 * Counting globally made only day A ever look like a first workout, so day B's
 * very first run — nothing about it tested yet — wrongly told the user their
 * starting weights were already set.
 *
 * @param isTestWorkout did the just-finished workout contain a strength/rep test set
 * @param splitDayCompletedCountIncludingCurrent how many times the user has completed
 *   THIS split day, counting the run just finished
 */
export function selectWorkoutCompletionMessage(
  isTestWorkout: boolean,
  splitDayCompletedCountIncludingCurrent: number,
): WorkoutCompletionMessage {
  // non-test workouts, and anything past this split day's second run, keep the generic message
  if (!isTestWorkout) return WorkoutCompletionMessage.Generic;
  if (splitDayCompletedCountIncludingCurrent === 1) return WorkoutCompletionMessage.FirstTest;
  if (splitDayCompletedCountIncludingCurrent === 2) return WorkoutCompletionMessage.SecondTest;
  return WorkoutCompletionMessage.Generic;
}

/** Inputs the "test workout" explainer card decision needs (#4060). */
export interface TestWorkoutExplainerInput {
  /** is a coach viewing a client's workout in view-only mode */
  isCoachManaging: boolean;
  /** has the workout on screen already been finished (Done or Skipped) */
  isWorkoutFinished: boolean;
  /** how many times the user has already completed THIS workout of the split */
  priorCompletionsOfThisWorkout: number;
}

/**
 * Decide whether to show the "Test workout to customize your program" header
 * card (#4060).
 *
 * Why this card exists: a calibration workout prescribes fewer sets than
 * normal, and new users read that reduced volume as a broken program and
 * abandon onboarding. Calibration happens PER EXERCISE, the first time each
 * exercise comes up — so every workout of the split is a calibration workout
 * on its own first outing, not just the user's first-ever workout. The card is
 * therefore shown once per split variant: once for A, once for B, once for C.
 *
 * Deliberately NOT part of this decision: what kind of sets the workout holds.
 * An earlier version only fired on a 1-5RM test set, but once progression
 * starts calling for retests a single workout can hold a mix of set types, so
 * that trigger made the card appear and vanish for reasons a user cannot
 * predict. Position in the split does not wobble, so it is the only trigger.
 *
 * "How many times has this workout of the split been completed" is the same
 * signal the sibling completion message uses (selectWorkoutCompletionMessage,
 * #4061). Both features were asked for per split day by the same reviewer, so
 * they deliberately read the same per-split-day count.
 */
export function shouldShowTestWorkoutExplainer(input: TestWorkoutExplainerInput): boolean {
  // a coach viewing a client's workout in view-only mode is not the person
  // being onboarded — and this card would render above, and so shadow, the
  // coach's own view-only card
  if (input.isCoachManaging) return false;

  // an already-finished workout is being looked back at, not done — explaining
  // how to approach it is pointless there
  if (input.isWorkoutFinished) return false;

  // show it only on this workout's very first outing
  return input.priorCompletionsOfThisWorkout === 0;
}

/**
 * Create a workout entry stub.
 * name, planReference, and exercises are NOT set - they are computed on-demand via
 * getEffectiveWorkoutName, getEffectivePlanReference, and getEffectiveExercises.
 * These are only persisted when the workout is saved to DB or changed via ChangeWorkout page.
 */
export function createWorkoutEntry(date_local: DateTime): Log_Workout {
  let entry = {} as Log_Workout;
  entry.time_utc = date_local.toUTC().toISO();
  entry.timeOffset_min = date_local.offset;
  entry.type = LogRecordType.Workout;
  entry.state = LogRecordState.Scheduled;
  entry.exercises = [];

  assureIdForEntry(entry);
  return entry;
}

/**
 * heal sparse `sets` arrays produced by Issue #720 — replace `null`/`undefined`
 * slots that precede a user-touched set with a `Skipped` placeholder.
 *
 * why: the user really did the later set, so the missing earlier slot was a
 * skip, not back-fill. preserves user intent without shifting the array.
 *
 * mutates in place. returns the indices that were healed (empty = no heal).
 * the indices form the diagnostic payload — callers should breadcrumb them
 * rather than the post-mutation `sets` array, which no longer shows the nulls.
 */
export function healSparseLogSets(sets: (Log_WorkoutSet | null | undefined)[] | undefined): number[] {
  if (!sets || sets.length === 0) return [];
  // find last user-touched set (Done/Skipped/Incomplete). intentionally
  // restricted to known user-touched states so the heal stays safe if the
  // LogRecordState enum grows new "future" siblings of Scheduled.
  let lastTouchedIndex = -1;
  for (let i = sets.length - 1; i >= 0; i--) {
    const s = sets[i];
    if (s && (s.state === LogRecordState.Done || s.state === LogRecordState.Skipped || s.state === LogRecordState.Incomplete)) {
      lastTouchedIndex = i;
      break;
    }
  }
  if (lastTouchedIndex <= 0) return [];
  const healedIndices: number[] = [];
  for (let i = 0; i < lastTouchedIndex; i++) {
    if (!sets[i]) {
      sets[i] = {
        state: LogRecordState.Skipped,
        stats: {},
        preStatus: [],
        postStatus: [],
      } as Log_WorkoutSet;
      healedIndices.push(i);
    }
  }
  return healedIndices;
}

/** Why `fillMissingEarlierSetsFromScreen` left a missing position empty. */
export enum MissingEarlierSetRefusal {
  /** the screen has no set at this position, or it is not Done or Skipped */
  NotConcludedOnScreen = 'not-concluded-on-screen',
  /** the screen's set was moved into another entry when the exercise was changed (#5507) */
  SplitOffBySwitch = 'split-off-by-switch',
}

export interface MissingEarlierSetsFill {
  /** earlier positions that were missing from the stored list before anything was filled */
  missingIndices: number[];
  /** positions now holding the screen's own set */
  filledIndices: number[];
  /** positions left missing, and why */
  refused: { index: number; reason: MissingEarlierSetRefusal }[];
}

/**
 * df-148d4d4ced87: before set `iSet` is written into the stored set list, put back every EARLIER
 * set the stored list is missing but the screen shows as concluded.
 *
 * Why: the stored list has been seen EMPTY while the screen showed earlier sets finished (Sentry
 * CYBERNETIC-FITNESS-APP-R9). Writing set 2 into an empty list leaves holes, and
 * `healSparseLogSets` then stores each hole as a blank Skipped set, so the numbers the user logged
 * are lost. The screen's own objects are the user's data, so they go back in.
 *
 * Only a MISSING position is filled (null, undefined, or past the end); a stored set is never
 * replaced. A screen set that was moved into another entry by an exercise change is refused: that
 * entry already holds a copy, so filling it here would store the same set under two exercises.
 *
 * Mutates `storedSets` in place. Returns what it found and did, for the caller to report.
 */
export function fillMissingEarlierSetsFromScreen(
  storedSets: (Log_WorkoutSet | null | undefined)[],
  iSet: number,
  screenSets: readonly (Log_WorkoutSet | null | undefined)[],
  splitOffSets: ReadonlySet<Log_WorkoutSet>,
): MissingEarlierSetsFill {
  const result: MissingEarlierSetsFill = { missingIndices: [], filledIndices: [], refused: [] };
  for (let i = 0; i < iSet; i++) {
    if (storedSets[i]) continue;
    result.missingIndices.push(i);
    const screenSet = screenSets[i];
    if (!screenSet || (screenSet.state !== LogRecordState.Done && screenSet.state !== LogRecordState.Skipped)) {
      result.refused.push({ index: i, reason: MissingEarlierSetRefusal.NotConcludedOnScreen });
      continue;
    }
    if (splitOffSets.has(screenSet)) {
      result.refused.push({ index: i, reason: MissingEarlierSetRefusal.SplitOffBySwitch });
      continue;
    }
    storedSets[i] = screenSet;
    result.filledIndices.push(i);
  }
  return result;
}

/** Which decisive answer the user gave to a plateau suggestion. "Decide later"
 *  is not in here on purpose — it leaves the card alone, so it never reaches
 *  this code. */
export enum PlateauResolutionOutcome {
  /** the user accepted the suggested change */
  Resolved = 'resolved',
  /** the user turned the suggested change down */
  Dismissed = 'dismissed',
}

/**
 * Removes from the given sets every post-set status the predicate says to remove, and returns
 * how many it removed in total.
 *
 * This is the one body behind every "this outcome is withdrawn, take it off the sets" helper in
 * the app. Each of those helpers is the same loop over the same shape and differs only in which
 * statuses it matches, so the loop lives here once and each helper supplies its own predicate.
 * Adding another such helper means writing a predicate, not another copy of this.
 *
 * Two things this does FOR the predicate, which is the reason it is worth sharing at all:
 *
 * - **It tolerates a damaged or old-shaped document.** A set may be missing from a sparse list,
 *   and a set may carry no status list at all. Both are skipped rather than trusted.
 * - **It normalises the LEGACY NUMBER form before the predicate ever sees a status.**
 *   `postStatus[].status` is typed `PostSetStatus | number` because a status written by an old
 *   version of the app is still stored as a number, and `remapOldPostSetStatusNumberToEnum` maps
 *   those numbers onto today's enum values. A predicate comparing enum values alone would keep an
 *   old status and the caller would end up with the very duplicate it cleared the set to prevent.
 *   Doing it here means a predicate cannot forget it. That mistake was made once already, in
 *   #5276, in a copy that has since been folded into this one.
 *
 * `??` rather than `||`, so a number the remap does not know is passed on as itself instead of
 * collapsing to a falsy value.
 */
export function removeMatchingPostStatuses(
  sets: Log_WorkoutSet[],
  shouldRemove: (status: PostSetStatus | number) => boolean,
): number {
  let removedCount = 0;
  // for each set, drop the statuses the predicate matches and count them
  for (const set of sets) {
    if (!set?.postStatus?.length) {
      continue;
    }
    const countBefore = set.postStatus.length;
    set.postStatus = set.postStatus.filter(s => !shouldRemove(normalizePostSetStatus(s.status)));
    removedCount += countBefore - set.postStatus.length;
  }
  return removedCount;
}

/** Turns a stored post-set status into today's enum value, so that a status written as a number
 *  by an old version of the app compares equal to the enum value it became. A number the remap
 *  does not know is returned as itself. */
function normalizePostSetStatus(status: PostSetStatus | number): PostSetStatus | number {
  return typeof status === 'number' ? remapOldPostSetStatusNumberToEnum[status] ?? status : status;
}

/**
 * Removes every `PlateauDetected` post-set status from the given sets, and
 * returns how many it removed.
 *
 * That status is the ONLY thing that draws the plateau "Resolve" card, so
 * anything that means "this card is finished with" has to take it off EVERY
 * set. It can sit on any set — it is pushed onto whichever set triggered the
 * deload — and a re-detection can leave it on more than one. Leaving a single
 * one behind leaves the card on screen (#4360).
 *
 * This is the plateau twin of `clearPendingVolumeAutoregulationStatuses` in
 * `volume-autoregulation.ts`, and keeps the same contract on purpose.
 */
export function clearPlateauDetectedStatuses(sets: Log_WorkoutSet[]): number {
  return removeMatchingPostStatuses(sets, status => status === PostSetStatus.PlateauDetected);
}

/**
 * Every post-set status that records a STRENGTH TEST'S VERDICT — whether the test was usable and
 * what it concluded. #5276 needs them because re-concluding a corrected strength test pushes a
 * fresh verdict without removing the old one, so the set would end up claiming two.
 *
 * `PostSetStatus.Weakness` is deliberately NOT here. It is not a verdict on the test; it records
 * that the exercise was swapped out of the user's program because the test derived a load below
 * zero. Removing it would delete the only account of a swap that has already happened.
 */
const STRENGTH_TEST_VERDICT_STATUSES: PostSetStatus[] = [
  PostSetStatus.NonRMStrengthTest,
  PostSetStatus.StrengthTestSuccess,
  PostSetStatus.StrengthTestSuccessLastSet,
  PostSetStatus.StrengthTestFailToManyReps,
  PostSetStatus.StrengthTestFailToManyRepsLastSet,
  PostSetStatus.StrengthTestFailToFewReps,
  PostSetStatus.StrengthTestFailToFewRepsLastSet,
];

/**
 * Whether one stored status is a strength-test verdict.
 *
 * Four of those verdicts can also be stored in the LEGACY NUMBER form on an old document, and
 * keeping one of those would leave a re-concluded set carrying two contradictory verdicts at once
 * — precisely on the documents most likely to be re-opened years later (#5276).
 * `removeMatchingPostStatuses` normalises a stored status before it reaches this predicate, so
 * comparing today's enum values here is enough.
 */
function isStrengthTestVerdict(status: PostSetStatus | number): boolean {
  return STRENGTH_TEST_VERDICT_STATUSES.some(verdict => verdict === status);
}

/**
 * Takes the strength-test verdict off the given sets, so that re-concluding a corrected test
 * writes its verdict onto a set that carries none rather than appending a second one.
 *
 * Same shape and same reasoning as `clearPlateauDetectedStatuses` above: a verdict is an OUTCOME
 * of concluding the set, so when the set is concluded again from corrected numbers the old
 * outcome is withdrawn first and whatever the new numbers deserve is written by the re-run.
 *
 * Returns how many statuses it removed, so a caller can tell whether the document it holds
 * actually changed.
 */
export function clearStrengthTestVerdictStatuses(sets: Log_WorkoutSet[]): number {
  return removeMatchingPostStatuses(sets, isStrengthTestVerdict);
}

/**
 * The post-set statuses that report what CONCLUDING the set did to this intensity's progression — a
 * deload, a weight below the recommendation, maxed-out equipment, a moved rep target.
 * df-50489fc01ec4 needs them because re-concluding a corrected set pushes these afresh without
 * removing the old ones: a failure corrected into a success kept its "weight backed off" badge
 * after the deload itself had been rewound, and a correction that still failed showed it twice.
 *
 * The list holds only statuses that a re-run of the conclusion writes again whenever the corrected
 * numbers still deserve them, so clearing them first loses nothing. Deliberately NOT here:
 * - `PlateauResolved` / `PlateauDismissed` — they record the user's own answer.
 * - `PlateauDetected` — withdrawn on its own, because a pending question has plan-side state.
 * - `Weakness` and `RepCeilingSwitch` — they record a swap that has already happened.
 * - the `VolumeAutoregulation*` statuses — they have their own guard and plan-side state.
 * - `SkippedSet`.
 * - `ImmediateProgression` / `ImmediateProgressionReps` — a follow-up set pushes them only while
 *   it is NOT being edited, so a correction never writes them again; clearing them would erase a
 *   badge that is still deserved.
 * - `UpdatedRepTargetHighRepSession` / `UpdatedRepTargetHeavySession` — they report that this set
 *   pushed a DIFFERENT intensity's rep target away. A correction rewinds only this intensity's
 *   path, so the other one keeps the value the first conclusion gave it, and the re-run usually
 *   finds nothing left to move and pushes no badge — clearing them would erase a change that still
 *   stands. (A rep test corrected UPWARDS does push further, which is a real second push; the
 *   badge is kept to one by `addPostStatusOnce` at the push site instead — df-09c86ce422e8.)
 * - the rep-test verdicts — `clearRepTestSuccessStatuses` and `clearRepTestWeightAnswerStatuses`
 *   below withdraw those, on two different conditions, for the reasons their own comments give.
 */
const PROGRESSION_OUTCOME_STATUSES: PostSetStatus[] = [
  PostSetStatus.ProgressionDeload,
  // #4822: the last-set wording of the same deload, pushed again on the same condition
  PostSetStatus.ProgressionDeloadLastSet,
  PostSetStatus.RegularSetLowerWeight,
  PostSetStatus.MaxedOutEquipment,
  PostSetStatus.UpdatedRepTargetRegularSession,
  // #5620: the note that a follow-up repeated a passed strength test. The re-run pushes it again
  // whenever the corrected numbers still earn it, so clearing it first loses nothing.
  PostSetStatus.StrengthTestFollowupDone,
  // #5979: a correction re-asks the ceiling question whenever the corrected set still reaches the
  // ceiling, so this one is written again. `RepCeilingSwitch` is deliberately NOT here: like
  // `Weakness`, it records a swap of the program's exercise that the correction does not undo.
  PostSetStatus.RepCeilingHeld,
];

/** Whether one stored status reports a progression outcome. The legacy number form is already
 *  normalised by `removeMatchingPostStatuses` before it gets here. */
function isProgressionOutcome(status: PostSetStatus | number): boolean {
  return PROGRESSION_OUTCOME_STATUSES.some(outcome => outcome === status);
}

/**
 * Takes the progression-outcome statuses off the given sets, so that re-concluding a corrected set
 * writes the ones the corrected numbers deserve onto a set that carries none of them.
 *
 * Same shape and same reasoning as `clearStrengthTestVerdictStatuses` above. Returns how many
 * statuses it removed.
 */
export function clearProgressionOutcomeStatuses(sets: Log_WorkoutSet[]): number {
  return removeMatchingPostStatuses(sets, isProgressionOutcome);
}

/** The statuses a deload taken at a plateau leaves: the card, or the user's recorded answer to it. */
const PLATEAU_STATUSES: PostSetStatus[] = [
  PostSetStatus.PlateauDetected,
  PostSetStatus.PlateauResolved,
  PostSetStatus.PlateauDismissed,
];

/** The two statuses an accepted deload leaves on the set that failed. */
const DELOAD_STATUSES: PostSetStatus[] = [
  PostSetStatus.ProgressionDeload,
  PostSetStatus.ProgressionDeloadLastSet,
];

/**
 * Whether one session's sets of an exercise hold the accepted deload that lowered the plan's
 * target weight one step (#4822).
 *
 * A deload status on its own is not enough. A deload taken at a plateau carries the same status but
 * lowers nothing, and the "Resolve" card that marks it can be cleared afterwards. What the stepping
 * deload does, and the plateau one does not, is store the failed set's strength figure as the path's
 * `lastDeload1rm_net_kg`. So the answer is yes only when a set carries a deload status AND its own
 * strength figure is that stored number, copied over unchanged.
 *
 * That match alone has one gap: a plateau set that repeats the earlier failed performance exactly
 * has the SAME strength figure as the one stored. So a session holding any plateau status (the card,
 * or the recorded answer to it) is refused as well. What remains is a plateau whose card was
 * cleared without a record AND whose figure matches exactly; that shows the note wrongly, which
 * costs a sentence, not a weight.
 *
 * Tolerates a sparse set list and a set with no status list or no stats, as the helpers above do.
 */
export function isSessionSteppedDownByDeload(
  sets: readonly (Log_WorkoutSet | null | undefined)[],
  lastDeload1rm_net_kg: number | undefined,
): boolean {
  if (lastDeload1rm_net_kg == null || !Number.isFinite(lastDeload1rm_net_kg)) {
    return false;
  }
  const hasStatus = (set: Log_WorkoutSet | null | undefined, statuses: PostSetStatus[]) =>
    !!set?.postStatus?.some(s => statuses.some(wanted => wanted === normalizePostSetStatus(s.status)));
  if (sets.some(set => hasStatus(set, PLATEAU_STATUSES))) {
    return false;
  }
  return sets.some(set =>
    set?.stats?.estimated1rm_net_kg === lastDeload1rm_net_kg && hasStatus(set, DELOAD_STATUSES));
}

/**
 * Whether the session that set today's target for this exercise was one whose accepted deload
 * lowered it one step (#4822). `entriesNewestFirst` holds this exercise's entry in each earlier
 * workout, newest first, with the workout being opened already left out.
 *
 * The session that counts is the newest one at this intensity in which the user performed at least
 * one set. A skipped or abandoned session in between changed nothing, so today's target still comes
 * from the one before it. An entry that records no intensity (written by an older app) is accepted,
 * since it cannot be told apart. Only that one session is judged; an older deload behind a newer
 * trained session has already been worked through.
 */
export function isDeloadCarriedOverFromLastSession(
  entriesNewestFirst: readonly (Log_WorkoutExercise | null | undefined)[],
  intensityLevel: IntensityLevel | undefined,
  lastDeload1rm_net_kg: number | undefined,
): boolean {
  const lastTrained = entriesNewestFirst.find(entry =>
    !!entry?.sets?.some(set => set?.state === LogRecordState.Done) &&
    (entry.pathLevel == null || entry.pathLevel === intensityLevel));
  return !!lastTrained && isSessionSteppedDownByDeload(lastTrained.sets, lastDeload1rm_net_kg);
}

/**
 * The two post-set statuses that report a REP TEST SUCCEEDING — "your rep range is established"
 * and its last-set wording. df-09c86ce422e8 needs them for the same reason the two lists above
 * exist: the rep-test branch of `concludeSet` pushes one of these and removes none, so correcting
 * a finished rep test left the old one standing. A correction that still succeeds showed the badge
 * twice, and a correction onto a weight far from the prescribed one left "rep range established"
 * on a set the re-run had just judged a failure.
 *
 * The three REMAINING rep-test statuses are deliberately NOT here, and the difference is the whole
 * reason this is its own list rather than five entries:
 * - `RepTestFailToHeavy` / `RepTestFailToLight` / `RepTestBadEquipment` record the user's own
 *   ANSWER to "why did this run at a different weight than we asked for". A correction asks
 *   nobody, so it records no answer of its own and can never write one of them back — it READS
 *   the recorded one instead (`recordedRepTestWeightAnswerStatus` below, df-6c87ae2df081).
 *   Clearing them here would delete an answer with nothing to restore it, and take the corrected
 *   set's own verdict with it. They are withdrawn instead by
 *   `clearRepTestWeightAnswerStatuses` below, in the one case where they are known to be wrong.
 */
const REP_TEST_SUCCESS_STATUSES: PostSetStatus[] = [
  PostSetStatus.RepTestSuccess,
  PostSetStatus.RepTestSuccessLastSet,
];

/** Whether one stored status reports a rep test succeeding. The legacy number form is already
 *  normalised by `removeMatchingPostStatuses` before it gets here — both of these have one
 *  (`6` and `7`), and those are exactly the old documents people re-open years later. */
function isRepTestSuccess(status: PostSetStatus | number): boolean {
  return REP_TEST_SUCCESS_STATUSES.some(verdict => verdict === status);
}

/**
 * Takes the rep-test success verdict off the given sets, so that re-concluding a corrected rep
 * test writes the verdict its corrected numbers earn onto a set that carries none.
 *
 * Same shape and same reasoning as `clearStrengthTestVerdictStatuses` above. Returns how many
 * statuses it removed.
 */
export function clearRepTestSuccessStatuses(sets: Log_WorkoutSet[]): number {
  return removeMatchingPostStatuses(sets, isRepTestSuccess);
}

/**
 * The post-set statuses that record what the user ANSWERED when asked why their rep test ran at a
 * weight far from the prescribed one: the prescription was too heavy, it was too light, or this
 * was the closest their equipment could get.
 *
 * Unlike every other list in this file these are NOT re-written by a re-run — a correction asks
 * the question of nobody. So the one caller withdraws them in a single case: the correction has
 * put the weight back ON target, which is the case where the premise of the question no longer
 * holds and the re-run is about to write a success verdict beside the old answer. While the
 * corrected weight is still off target the answer still describes the set and is left alone.
 *
 * Because they survive a correction, they are also the ONLY record of what the user answered, and
 * `recordedRepTestWeightAnswerStatus` below reads them back so a correction can judge the set the
 * same way the first conclusion did (df-6c87ae2df081).
 */
const REP_TEST_WEIGHT_ANSWER_STATUSES: PostSetStatus[] = [
  PostSetStatus.RepTestFailToHeavy,
  PostSetStatus.RepTestFailToLight,
  PostSetStatus.RepTestBadEquipment,
];

/** Whether one stored status records the user's answer about a rep test's weight. Two of these
 *  have a legacy number form (`8` and `9`), already normalised before this is called. */
function isRepTestWeightAnswer(status: PostSetStatus | number): boolean {
  return REP_TEST_WEIGHT_ANSWER_STATUSES.some(answer => answer === status);
}

/**
 * Takes the rep-test weight answer off the given sets. Call it ONLY when the weight the set now
 * records is on target, because nothing writes these back — see the list's own comment.
 *
 * Returns how many statuses it removed.
 */
export function clearRepTestWeightAnswerStatuses(sets: Log_WorkoutSet[]): number {
  return removeMatchingPostStatuses(sets, isRepTestWeightAnswer);
}

/**
 * The status recording what the user answered about this rep test's weight, or `undefined` when
 * the set carries none. Returns today's enum value even for a status an old version of the app
 * stored as a number.
 *
 * It exists because a correction asks the user nothing, so the status the FIRST conclusion wrote
 * is the only surviving account of the answer. Re-concluding a corrected set reads it back through
 * this and judges the weight the way the user's own answer did, instead of falling back to "the
 * weight is off target, so the set failed" and quietly taking away the progression the answer
 * earned (df-6c87ae2df081).
 *
 * It lives here, beside the list it reads, because the legacy-number normalisation is private to
 * this file — and an old document is exactly the kind people re-open and correct.
 */
export function recordedRepTestWeightAnswerStatus(set: Log_WorkoutSet): PostSetStatus | undefined {
  // tolerate a document written without the list, the way removeMatchingPostStatuses does
  if (!set?.postStatus?.length) {
    return undefined;
  }
  // for each stored status, answer with the first one that records an answer
  for (const stored of set.postStatus) {
    const status = normalizePostSetStatus(stored.status);
    if (isRepTestWeightAnswer(status)) {
      return status as PostSetStatus;
    }
  }
  return undefined;
}

/**
 * Adds one post-set status to a set unless the set already carries it, and says whether it added.
 *
 * This is the other half of the "a re-conclusion must not leave two of the same badge" problem, for
 * the badges that CANNOT be cleared first. `UpdatedRepTargetHighRepSession` and
 * `UpdatedRepTargetHeavySession` report that this set pushed a DIFFERENT intensity's rep target
 * away, and a correction rewinds only the intensity that was trained — so the other path keeps the
 * value it was given and clearing the badge would erase a change that still stands
 * (df-09c86ce422e8). A rep test corrected UPWARDS pushes that other path further still, which is a
 * real second push, but the user does not need to be told the same thing twice.
 *
 * It compares through the same legacy-number normalisation `removeMatchingPostStatuses` applies, so
 * a badge stored by an old version of the app counts as already present.
 *
 * `textInserts` is deliberately not a parameter: a status carrying text says something specific
 * about ONE conclusion (a weight, a rep count), so "the set already has this one" is not a safe
 * answer for it. Use this only for badges with no inserts.
 */
export function addPostStatusOnce(set: Log_WorkoutSet, status: PostSetStatus): boolean {
  if (!set) {
    return false;
  }
  // tolerate a document written without the list, the way removeMatchingPostStatuses does
  if (!set.postStatus) {
    set.postStatus = [];
  }
  if (set.postStatus.some(s => normalizePostSetStatus(s.status) === status)) {
    return false;
  }
  set.postStatus.push({ status, textInserts: undefined });
  return true;
}

/**
 * Applies a decisive plateau answer to one exercise's sets: takes the
 * `PlateauDetected` status off (that status is the ONLY thing that draws the
 * "Resolve" card), and leaves a `PlateauResolved` / `PlateauDismissed` status
 * behind as the historical record of what the user chose.
 *
 * Returns how many `PlateauDetected` statuses it removed, so the caller can
 * tell whether the workout document it holds actually changed and needs saving.
 *
 * Tolerates a set list with holes in it, or a set with no status list, for the
 * same reason `clearPlateauDetectedStatuses` does: sparse `Log_WorkoutSet[]` are
 * a real shape in the field (see `healSparseLogSets` and #720). Taking the card
 * away always matters more than leaving the record of the answer, so a missing
 * status list costs the record and nothing else.
 */
export function applyPlateauResolution(
  sets: Log_WorkoutSet[],
  outcome: PlateauResolutionOutcome,
  resolvedMessage?: string,
): { removedCount: number } {
  if (!sets?.length) {
    return { removedCount: 0 };
  }

  // remember which set was showing the card BEFORE sweeping, so the record of
  // the user's answer lands on that set rather than blindly on the first one
  const carryingIndex = sets.findIndex(s => s?.postStatus?.some(p => p.status === PostSetStatus.PlateauDetected));
  const removedCount = clearPlateauDetectedStatuses(sets);

  // if no set was showing the card, fall back to the first set — this exercise
  // is being resolved from somewhere else (the plan still pointed here), and the
  // record of the answer still belongs on it
  const badgeSet = carryingIndex === -1 ? sets[0] : sets[carryingIndex];

  // if that set has no status list to put the record in, stop here. The card is
  // already gone, which is the part the user sees
  if (!badgeSet?.postStatus) {
    return { removedCount };
  }

  // if the user accepted, record what the app is going to do; if they turned it
  // down, record that instead
  if (outcome === PlateauResolutionOutcome.Resolved) {
    badgeSet.postStatus.push({ status: PostSetStatus.PlateauResolved, textInserts: { ["message"]: resolvedMessage ?? '' } });
  } else {
    badgeSet.postStatus.push({ status: PostSetStatus.PlateauDismissed });
  }

  return { removedCount };
}

/**
 * Check if an exercise has been started (any set is non-Scheduled).
 *
 * Tolerates a hole in the set list, for the same reason
 * `clearPlateauDetectedStatuses` does: sparse `Log_WorkoutSet[]` are a real
 * shape in the field (see `healSparseLogSets` and #720), and `healSparseLogSets`
 * deliberately leaves a hole alone when no later set was ever touched — so a
 * hole reaches here even where the heal has run. A hole is an entry the user
 * never wrote, so it counts as not started; a real Done set elsewhere in the
 * list still reports the exercise as started.
 *
 * Tolerates a missing set list, and a missing exercise, the same way: a stored
 * workout an older version wrote can hold either (df-1a9b71682623), and neither
 * holds anything the user wrote, so both count as not started.
 */
export function isExerciseStarted(exercise: Log_WorkoutExercise | null | undefined): boolean {
  const sets = exercise?.sets;
  return !!sets && sets.length > 0 && sets.some(s =>
    s?.state === LogRecordState.Done ||
    s?.state === LogRecordState.Incomplete ||
    s?.state === LogRecordState.Skipped
  );
}

/**
 * A stored exercise list without its holes: the null entries an older version wrote
 * (df-1a9b71682623). A hole holds nothing the user wrote, and exercises are matched by
 * sub-slot, not position, so dropping one renumbers nothing.
 *
 * Hands back the very same array when there is no hole, so a caller that writes through the
 * stored list keeps doing so; only a damaged list gets a new array. Used where a workout's
 * stored list is read as it is rather than merged with the plan (#6574) — the merge drops
 * holes the same way.
 */
export function storedExercisesWithoutHoles(
  exercises: (Log_WorkoutExercise | null | undefined)[],
): Log_WorkoutExercise[] {
  if (exercises.every(e => !!e)) return exercises as Log_WorkoutExercise[];
  return exercises.filter((e): e is Log_WorkoutExercise => !!e);
}

/**
 * What separates a split entry's sub-slot id from the id it was split off. See
 * `splitLogExerciseAtSwitch` below and `design-docs/exercise-replacement.md`, "An exercise
 * changed after sets were logged is split (#5507)".
 *
 * A program's sub-slot ids are always `$<slot>$<number>`, so an id carrying this can never be
 * one the program hands out, and `parseSubslotIdAndOffset` still reads the slot and index in
 * front of it because its pattern is not anchored at the end.
 */
export const SPLIT_SUB_SLOT_SEPARATOR = '#s';

/** Whether this sub-slot id belongs to an entry split off by an exercise change (#5507), which the
 *  program never holds on purpose. */
export function isSplitSubSlotId(subSlotId: string | null | undefined): boolean {
  return typeof subSlotId === 'string' && subSlotId.includes(SPLIT_SUB_SLOT_SEPARATOR);
}

/**
 * A sub-slot id for a new split entry: `<baseId>#s<n>`, with the lowest `n` from 1 up that no
 * entry of this workout already uses. The id must be unique inside the workout, because the
 * plan merge keys its entries by id and silently keeps only one of two that share it.
 *
 * When the base is itself a split id (never expected — only the entry the program holds is ever
 * switched), the part in front of the separator is used, so ids do not grow `#s1#s1`.
 */
export function mintSplitSubSlotId(baseSubSlotId: string, existingSubSlotIds: readonly (string | undefined)[]): string {
  const base = baseSubSlotId.split(SPLIT_SUB_SLOT_SEPARATOR)[0];
  const taken = new Set(existingSubSlotIds.filter(id => !!id));
  // for each number from 1 up, take the first id nobody uses yet
  for (let n = 1; ; n++) {
    const candidate = `${base}${SPLIT_SUB_SLOT_SEPARATOR}${n}`;
    if (!taken.has(candidate)) {
      return candidate;
    }
  }
}

/** Whether an exercise change on this entry must first split its finished sets off (#5507):
 *  true as soon as one set was performed and concluded. */
export function hasSetsToKeepBeforeSwitch(entry: Log_WorkoutExercise | null | undefined): boolean {
  return !!entry?.sets?.some(s => s?.state === LogRecordState.Done);
}

/**
 * Split a workout entry in two because the user is changing its exercise after logging sets
 * (#5507), so the performed sets stay with the exercise they were performed on.
 *
 * - **`finished`** is a NEW entry: the old exercise, its performed sets, its plan copy, a split id,
 *   every set not performed marked Skipped, no open "Resolve" question.
 * - **`fresh`** is the ORIGINAL object, emptied in place so the new exercise starts at set 1.
 *
 * Why each of those, and what the caller must do with the two: `design-docs/exercise-replacement.md`,
 * "An exercise changed after sets were logged is split (#5507)". Only call it when
 * `hasSetsToKeepBeforeSwitch` says so.
 *
 * @param entry the workout entry about to change exercise; emptied in place
 * @param existingSubSlotIds every sub-slot id the workout's entries already use
 * @param planEntryIfNoCopy the exercise's current plan entry, copied onto `finished` when the entry
 *   has no saved plan copy yet — that copy is taken when set 1 is concluded, so an exercise whose
 *   first performed set was a later one has none, and without one it could not be drawn
 * @param shownSetCount how many sets the screen shows for this exercise. A set is stored only once
 *   it is performed, so the stored list is usually shorter; `finished` is padded with Skipped sets
 *   up to this count, because the loader would otherwise pad it with sets still to do
 */
export function splitLogExerciseAtSwitch(
  entry: Log_WorkoutExercise,
  existingSubSlotIds: readonly (string | undefined)[],
  planEntryIfNoCopy?: Plan_SubSlot,
  shownSetCount = 0,
): { finished: Log_WorkoutExercise; fresh: Log_WorkoutExercise } {
  const finished = deepClone(entry);
  finished.subSlotId = mintSplitSubSlotId(entry.subSlotId, existingSubSlotIds);
  if (!finished.planSubslotBackup && planEntryIfNoCopy) {
    finished.planSubslotBackup = deepClone(planEntryIfNoCopy);
  }
  // an open plateau question on the copy is about the exercise being changed away from; left
  // there it would claim a card is open that nothing can show or answer
  if (finished.planSubslotBackup) {
    delete finished.planSubslotBackup.plateauWorkoutLog_id;
  }
  // a damaged entry with no set list gets an empty one rather than a crash
  finished.sets = finished.sets ?? [];
  // for each set not performed, mark it skipped. a hole in a sparse set list is kept as a hole:
  // removing it would renumber the sets after it
  for (const set of finished.sets) {
    if (set && set.state !== LogRecordState.Done) {
      set.state = LogRecordState.Skipped;
    }
  }
  // for each set the screen shows but the log does not hold yet, add it as skipped
  while (finished.sets.length < shownSetCount) {
    finished.sets.push({ state: LogRecordState.Skipped, stats: {}, preStatus: [], postStatus: [] });
  }
  removeMatchingPostStatuses(finished.sets, status =>
    status === PostSetStatus.VolumeAutoregulationPending || status === PostSetStatus.PlateauDetected);
  // the answer belongs to the exercise the question was about, and that one keeps no open question
  delete finished.volumeAutoregulationAnswered;

  entry.sets = [];
  delete entry.planSubslotBackup;
  // the fresh half has trained nothing yet; its first concluded set takes a new one
  delete entry.planSubslotAfterSession;
  delete entry.numPlannedSets;
  delete entry.volumeAutoregulationAnswered;

  return { finished, fresh: entry };
}

/**
 * Every entry of this workout that performed this exercise.
 *
 * Usually one, but an exercise changed after sets were logged leaves its sets in a split entry
 * (#5507), and changing back (A → B → A) then gives the same exercise two entries in one
 * workout. A reader that stops at the first match would miss the second entry's sets.
 */
export function entriesForExercise(
  exercises: readonly (Log_WorkoutExercise | null | undefined)[] | null | undefined,
  exerciseId: string,
): Log_WorkoutExercise[] {
  return (exercises ?? []).filter((e): e is Log_WorkoutExercise => !!e && e.exerciseId === exerciseId);
}

/**
 * This exercise's part of one workout as a single entry, for a reader that shows one row per
 * workout: the entry itself when there is one, and when there are several (#5507, see
 * `entriesForExercise`) a read-only copy of the first carrying the sets of all of them, in list
 * order — which is the order they were performed, because a split entry is placed above the one
 * that replaced it. Undefined when the workout does not hold the exercise.
 *
 * Never save what this returns: for several entries it is a stand-in, not a stored entry.
 */
export function combinedEntryForExercise(
  exercises: readonly (Log_WorkoutExercise | null | undefined)[] | null | undefined,
  exerciseId: string,
): Log_WorkoutExercise | undefined {
  const entries = entriesForExercise(exercises, exerciseId);
  if (entries.length <= 1) {
    return entries[0];
  }
  return { ...entries[0], sets: entries.flatMap(e => e.sets ?? []) };
}

/**
 * Deciding whether a workout is FINISHED, and repairing one whose state field
 * says it is not, lives in `workout-conclusion.ts` beside this file. That is one
 * subject with one rule behind it — only the workout screen can tell a finished
 * session from an abandoned one — and it is kept together so the rule is stated
 * once.
 */

/** What `mergeScreenExercisesOverStored` produced, and what it had to rescue. */
export interface MergedWorkoutExercises {
  /** the list to store: the screen's version of everything it holds, plus what only the document held */
  exercises: Log_WorkoutExercise[];
  /** the entries kept from the document because the screen did not hold them */
  carriedOver: Log_WorkoutExercise[];
}

/**
 * Build the exercise list a workout save should store, from what the SCREEN
 * holds and what the DOCUMENT holds.
 *
 * The workout page saves by writing its on-screen exercises into the document,
 * because the screen holds the authoritative set data — a canonicalizing read
 * can replace the document's exercise array with objects freshly loaded from
 * disk while the user is part-way through a set, so the page's own objects are
 * the newer ones. That much is unchanged here: for every exercise the screen
 * holds, the screen's object wins outright.
 *
 * What changed is what happens to an exercise the screen does NOT hold. The save
 * used to write the on-screen list over the stored one, so such an exercise was
 * deleted from the document along with every set the user had logged in it, with
 * nothing reporting it. The screen is short whenever the loader could not display
 * an exercise — its entry in the program is gone and the workout carries no
 * snapshot of it, or loading that one exercise threw, which the user sees as an
 * "exercises skipped" alert and has every reason to carry on past. So an
 * exercise only the document holds is KEPT.
 *
 * # Why keeping cannot resurrect something the user deleted
 *
 * This only ever keeps entries that are already in the list it was handed, which
 * is the document's own current list. It never invents an entry and never reads
 * an older copy, so an exercise that was deliberately removed is not here to be
 * kept: every route that removes one — changing which workout of the split this
 * is, and the plan merge that drops an unstarted exercise the program no longer
 * has — rewrites that list itself.
 *
 * That holds as long as the list handed in is still the one those routes wrote,
 * and there is one window where it is not. Changing which workout of the split
 * this is rewrites the list WITHOUT saving, so a canonicalizing read landing
 * before the next save puts the pre-change list back — and these entries are
 * then kept, giving a document that holds exercises from two days of the split.
 * It is bounded rather than closed: that route is reachable only while the
 * workout is still stored as never started, so the entries it brings back carry
 * no logged sets, and the plan merge discards them again the next time the
 * workout loads. Nothing here can tell that list apart from an ordinary one, so
 * do not read the paragraph above as a guarantee that survives a graft.
 *
 * # Retired ids, which is the one thing that has to be passed in
 *
 * A sub-slot id is NOT a permanent name for an exercise entry. Switching to an
 * exercise in a different slot mints a new sub-slot and renames the entry's
 * `subSlotId` in place (`performExerciseSwitchAsync`). Normally that renames the
 * document's entry too, because it is the same object — but in exactly the
 * stale-copy window described above it is not, and the document is left holding
 * the pre-switch entry under the OLD id while the screen holds it under the new
 * one. Keeping both would DUPLICATE the user's concluded sets, which is worse
 * than the loss this function exists to stop. So the page passes in the ids the
 * switch retired, and an entry under one of those is dropped rather than kept.
 *
 * Matching is by object identity first and sub-slot id second. The screen's
 * exercise objects usually ARE the document's, so the identity pass settles the
 * ordinary rename with nothing else needed, and it also matches an entry that
 * carries no id at all, which no id comparison could.
 *
 * An exercise only the screen holds is appended at the end — except a split entry (#5507,
 * `isSplitSubSlotId`), which goes directly above the entry that follows it on screen, the exercise
 * that replaced it. The document's list is replaced by a copy from disk often enough that the
 * split entry the page inserted can be missing from it by the time of the save.
 *
 * A screen match is consumed once. Two stored entries can share one sub-slot id
 * in an older document — the duplicate-id heal is plan-side, and the plan merge
 * that would dedupe them never runs for a finished workout — and emitting one
 * screen object into two positions would make a later save write that exercise's
 * sets twice.
 *
 * @param storedExercises  the exercise list the document currently holds
 * @param onScreenExercises the exercise list the page managed to load, in screen order
 * @param retiredSubSlotIds ids an exercise switch renamed away from during this page's life
 */
export function mergeScreenExercisesOverStored(
  storedExercises: readonly Log_WorkoutExercise[],
  onScreenExercises: readonly Log_WorkoutExercise[],
  retiredSubSlotIds: readonly string[] = [],
): MergedWorkoutExercises {
  const retired = new Set(retiredSubSlotIds.filter(id => !!id));
  const unmatched = [...onScreenExercises];
  const exercises: Log_WorkoutExercise[] = [];
  const carriedOver: Log_WorkoutExercise[] = [];

  // walk the DOCUMENT's order, so the stored list keeps the shape it had
  for (const stored of storedExercises) {
    if (!stored) continue;

    // take the screen's version of this entry if there is one, and use it up
    let matchIndex = unmatched.indexOf(stored);
    if (matchIndex < 0 && stored.subSlotId) {
      matchIndex = unmatched.findIndex(e => e?.subSlotId === stored.subSlotId);
    }
    if (matchIndex >= 0) {
      exercises.push(unmatched[matchIndex]);
      unmatched.splice(matchIndex, 1);
      continue;
    }

    // the screen does not hold it. Drop it only when a switch renamed this entry
    // away and the document is holding the copy from before that rename.
    if (stored.subSlotId && retired.has(stored.subSlotId)) continue;

    exercises.push(stored);
    carriedOver.push(stored);
  }

  // an exercise the screen holds and the document does not is still the user's
  for (const screenOnly of unmatched) {
    if (screenOnly) exercises.push(screenOnly);
  }

  // #5507: a split entry belongs directly above the exercise that replaced it, which is the one
  // after it on screen. Walk them last to first, so of two split entries in a row the later one is
  // placed first and the earlier one then lands above it. If what follows it on screen is not in
  // the list, it stays at the end.
  const screenOnlySplitEntries = unmatched.filter(e => !!e && isSplitSubSlotId(e.subSlotId));
  for (const splitEntry of screenOnlySplitEntries.reverse()) {
    const follower = onScreenExercises[onScreenExercises.indexOf(splitEntry) + 1];
    if (!follower || exercises.indexOf(follower) < 0) continue;
    exercises.splice(exercises.indexOf(splitEntry), 1);
    exercises.splice(exercises.indexOf(follower), 0, splitEntry);
  }

  return { exercises, carriedOver };
}

/**
 * Merge existing workout exercises with updated plan.
 * Returns merged exercise list and whether any changes were made.
 *
 * Rules:
 * - Walk plan subSlotIds in order (defines output order)
 * - For each plan subSlotId: reuse existing exercise if present & started, create stub if new/unstarted-swapped
 * - Started exercises removed from plan: append at end
 * - Unstarted exercises removed from plan: discard
 * - Same subSlotId with different exercise_id: keep old if started, new stub if unstarted
 * - A started split entry (#5507, `isSplitSubSlotId`) is never in the plan, but instead of the end
 *   it goes directly before the entry that followed it in the stored list — the exercise that
 *   replaced it. If that entry is gone it goes to the end like any other.
 * - A hole in the stored list (a null entry an older version wrote, df-1a9b71682623)
 *   holds nothing the user wrote, so it is unstarted and discarded like any other
 *   unstarted entry. Exercises are matched by subSlotId, not by position, so this
 *   renumbers nothing — unlike a hole in a SET list, which must never be removed.
 */
export function mergeWorkoutExercisesWithPlan(
  existingExercises: (Log_WorkoutExercise | null | undefined)[],
  planSubSlotIds: string[],
  planSubSlots: { [subSlotId: string]: Plan_SubSlot },
): { mergedExercises: Log_WorkoutExercise[]; changed: boolean } {
  const existingBySubSlotId = new Map<string, Log_WorkoutExercise>();
  for (const ex of existingExercises) {
    if (!ex) continue;
    existingBySubSlotId.set(ex.subSlotId, ex);
  }

  const consumed = new Set<string>();
  const merged: Log_WorkoutExercise[] = [];

  // walk plan subSlotIds in order
  for (const subSlotId of planSubSlotIds) {
    const existing = existingBySubSlotId.get(subSlotId);
    consumed.add(subSlotId);

    if (existing) {
      if (isExerciseStarted(existing)) {
        // started: keep as-is regardless of exercise_id change
        merged.push(existing);
      } else {
        // unstarted: check if exercise was swapped
        const planSubSlot = planSubSlots[subSlotId];
        const planExerciseId = planSubSlot?.exercise_id;
        if (existing.exerciseId && planExerciseId && existing.exerciseId !== planExerciseId) {
          // exercise was swapped, create new stub
          merged.push({ subSlotId, sets: [] } as Log_WorkoutExercise);
        } else {
          // same exercise or no exerciseId set yet, keep existing
          merged.push(existing);
        }
      }
    } else {
      // new exercise added to plan
      merged.push({ subSlotId, sets: [] } as Log_WorkoutExercise);
    }
  }

  // collect unconsumed existing exercises
  const keptSplitEntries: Log_WorkoutExercise[] = [];
  for (const ex of existingExercises) {
    if (ex && !consumed.has(ex.subSlotId)) {
      if (isExerciseStarted(ex)) {
        // started exercises removed from plan: append at end
        merged.push(ex);
        if (isSplitSubSlotId(ex.subSlotId)) {
          keptSplitEntries.push(ex);
        }
      }
      // unstarted exercises removed from plan: discard
    }
  }

  // #5507: a split entry holds the sets of an exercise the user changed away from, and belongs
  // directly above the exercise that replaced it — which is the entry that followed it in the
  // stored list. Walk them last to first, so that of two split entries in a row the later one is
  // placed first and the earlier one then lands above it.
  for (const splitEntry of [...keptSplitEntries].reverse()) {
    const storedIndex = existingExercises.indexOf(splitEntry);
    const follower = existingExercises.slice(storedIndex + 1).find(e => !!e);
    // the follower may have been replaced by a fresh stub for the same plan entry above, so
    // match it by sub-slot id when it is not the same object
    const findFollower = () => follower
      ? merged.findIndex(e => e === follower || e.subSlotId === follower.subSlotId)
      : -1;
    // if what followed it is no longer in the workout, it stays at the end
    if (findFollower() < 0) {
      continue;
    }
    merged.splice(merged.indexOf(splitEntry), 1);
    merged.splice(findFollower(), 0, splitEntry);
  }

  // detect whether anything changed
  let changed = merged.length !== existingExercises.length;
  if (!changed) {
    for (let i = 0; i < merged.length; i++) {
      if (merged[i] !== existingExercises[i]) {
        changed = true;
        break;
      }
    }
  }

  return { mergedExercises: merged, changed };
}